/**
 * @file useCredits.tsx
 *
 * @fileoverview Exports current user credits hook.
 */

import { ApolloQueryResult, useQuery } from '@apollo/client';
import GET_CREDITS from '~/jobiqo/components/Credits/graphql/queries/getCredits.gql';
import { FormattedMessage } from 'react-intl';
import { Credit } from '~/jobiqo/types/types';
import { isRecruiter } from '~/jobiqo/lib/auth';
import { User } from '~/jobiqo/models/user';
import { MessageAlert } from '~/vcl/atoms/02-messages/01-alert';

export interface UseCreditsInterface {
  /**
   * The loading flag.
   */
  loading: boolean;
  /**
   * The Error component.
   */
  Error: React.ReactNode;
  /**
   * The available credits.
   */
  data: Credit[];
  /**
   * The apollo refetch function to get subscriptions.
   */
  refetch?: () => Promise<ApolloQueryResult<any>>;
}

export default function useCredits(
  user: User | undefined,
  organizationId?: number,
): UseCreditsInterface {
  let Error: React.ReactNode = null;

  const { data, error, loading, refetch } = useQuery(GET_CREDITS, {
    // If the user is not recruiter avoid running queries.
    skip: !(user && isRecruiter(user)),
    fetchPolicy: 'no-cache',
    // Needed because refetch does not update loading state.
    notifyOnNetworkStatusChange: true,
    variables: {
      organizationId,
    },
  });

  if (error) {
    Error = (
      <MessageAlert alertStyle="danger">
        <FormattedMessage
          id="credits_recruiter_table_error"
          defaultMessage="There was an error getting your credits."
        />
      </MessageAlert>
    );
  }

  return {
    data: data?.credits ?? [],
    loading,
    Error,
    refetch,
  };
}
